"use client";

import React from "react";
import { PageContainer } from "@/src/components/layout/page-container";
import { Section } from "@/src/components/layout/section";
import { Text } from "@/src/components/information/text";
import { Badge } from "@/src/components/information/badge";
import { SectionCard } from "@/src/components/container/section-card";
import { CardTitle } from "@/src/components/container/card";
import { ChartProvider } from "@/src/components/charts/chart-provider";
import { BarChart } from "@/src/components/charts/bar-chart";
import { Link } from "react-router-dom";

const quickStartData = {
  categories: ["Jan", "Feb", "Mar"],
  series: [
    { name: "Revenue", data: [420, 510, 580] },
    { name: "Expenses", data: [310, 360, 390] },
  ],
};

const chartTypes = [
  {
    name: "Bar Chart",
    path: "/docs/charts/bar",
    description: "Vertical or horizontal bars for categorical data comparison",
  },
  {
    name: "Line Chart",
    path: "/docs/charts/line",
    description: "Trends and patterns over time with smooth or straight lines",
  },
  {
    name: "Area Chart",
    path: "/docs/charts/area",
    description: "Filled areas under lines showing magnitude and trends",
  },
  {
    name: "Pie Chart",
    path: "/docs/charts/pie",
    description: "Part-to-whole relationships with proportional slices",
  },
  {
    name: "Donut Chart",
    path: "/docs/charts/donut",
    description: "Pie chart variant with hollow center for additional content",
  },
  {
    name: "Gauge Chart",
    path: "/docs/charts/gauge",
    description: "Single value with threshold indicators and progress display",
  },
];

export default function DocsChartsPage() {
  return (
    <ChartProvider>
      <div className="space-y-theme-5 overflow-auto h-full py-theme-6 px-theme-6">
        {/* Header */}
        <Section>
          <div className="space-y-2">
            <Text as="h1" className="text-2xl font-bold tracking-tight">
              Charts
            </Text>
            <Text className="text-base text-muted-foreground max-w-3xl">
              ECharts-based data visualization components with CSS token integration.
              Build interactive, responsive charts with automatic theming support.
            </Text>
          </div>
        </Section>

        {/* Quick Start Example */}
        <Section title="Quick Start">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-theme-4">
            <SectionCard
              header={<CardTitle>Basic BarChart</CardTitle>}
              contentClassName="p-theme-5"
            >
              <BarChart data={quickStartData} height={300} />
            </SectionCard>

            <SectionCard
              header={<CardTitle>Setup Pattern</CardTitle>}
              contentClassName="p-theme-5"
            >
              <pre className="bg-muted p-theme-3 rounded-lg text-xs overflow-x-auto">
                <code>{`import { ChartProvider } from "@/src/components/charts/chart-provider";
import { BarChart } from "@/src/components/charts/bar-chart";

export default function MyDashboard() {
  const data = {
    categories: ["Q1", "Q2", "Q3"],
    series: [
      { name: "Revenue", data: [420, 510, 580] }
    ]
  };

  return (
    <ChartProvider>
      <BarChart data={data} height={400} />
    </ChartProvider>
  );
}`}</code>
              </pre>
            </SectionCard>
          </div>
        </Section>

        {/* Chart Types Grid */}
        <Section title="Available Chart Types">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-theme-4">
            {chartTypes.map((chart) => (
              <Link key={chart.path} to={chart.path}>
                <SectionCard
                  className="h-full hover:shadow-lg transition-shadow cursor-pointer"
                  header={<CardTitle className="text-lg">{chart.name}</CardTitle>}
                  contentClassName="p-theme-4"
                >
                  <Text className="text-sm text-muted-foreground">
                    {chart.description}
                  </Text>
                  <div className="mt-theme-3">
                    <Badge variant="outline">View Documentation</Badge>
                  </div>
                </SectionCard>
              </Link>
            ))}
          </div>
        </Section>

        {/* Token Bridge Concept */}
        <Section title="Token Bridge: CSS to ECharts">
          <SectionCard
            header={<CardTitle>Design Token Integration</CardTitle>}
            contentClassName="p-theme-5"
          >
            <div className="space-y-theme-3 text-sm">
              <Text>
                The <code className="bg-muted px-1.5 py-0.5 rounded text-xs">ChartProvider</code> component
                automatically bridges your CSS design tokens to ECharts theming:
              </Text>

              <div className="space-y-theme-2">
                <div className="bg-muted/50 p-theme-3 rounded-lg">
                  <p className="font-semibold text-xs mb-1">Colors</p>
                  <p className="text-muted-foreground text-xs">
                    Primary colors, accent colors, semantic colors (success, warning, error)
                  </p>
                </div>

                <div className="bg-muted/50 p-theme-3 rounded-lg">
                  <p className="font-semibold text-xs mb-1">Typography</p>
                  <p className="text-muted-foreground text-xs">
                    Font family, sizes, and weights from your theme configuration
                  </p>
                </div>

                <div className="bg-muted/50 p-theme-3 rounded-lg">
                  <p className="font-semibold text-xs mb-1">Dark Mode</p>
                  <p className="text-muted-foreground text-xs">
                    Automatic detection and switching of ECharts theme on light/dark mode change
                  </p>
                </div>
              </div>

              <Text className="text-xs text-muted-foreground pt-2">
                No manual configuration needed—charts automatically adapt to theme changes via
                MutationObserver watching the document element.
              </Text>
            </div>
          </SectionCard>
        </Section>

        {/* Common Props */}
        <Section title="Common Properties">
          <SectionCard
            header={<CardTitle>Shared Props Across Charts</CardTitle>}
            contentClassName="p-theme-5 overflow-x-auto"
          >
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 px-2 font-semibold">Property</th>
                  <th className="text-left py-2 px-2 font-semibold">Type</th>
                  <th className="text-left py-2 px-2 font-semibold">Default</th>
                  <th className="text-left py-2 px-2 font-semibold">Description</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b hover:bg-muted/50">
                  <td className="py-2 px-2 font-mono">title</td>
                  <td className="py-2 px-2">string</td>
                  <td className="py-2 px-2">—</td>
                  <td className="py-2 px-2">Chart title displayed at top</td>
                </tr>
                <tr className="border-b hover:bg-muted/50">
                  <td className="py-2 px-2 font-mono">subtitle</td>
                  <td className="py-2 px-2">string</td>
                  <td className="py-2 px-2">—</td>
                  <td className="py-2 px-2">Subtitle below title</td>
                </tr>
                <tr className="border-b hover:bg-muted/50">
                  <td className="py-2 px-2 font-mono">height</td>
                  <td className="py-2 px-2">string | number</td>
                  <td className="py-2 px-2">100%</td>
                  <td className="py-2 px-2">Chart container height (px or CSS value)</td>
                </tr>
                <tr className="border-b hover:bg-muted/50">
                  <td className="py-2 px-2 font-mono">className</td>
                  <td className="py-2 px-2">string</td>
                  <td className="py-2 px-2">—</td>
                  <td className="py-2 px-2">Additional CSS classes for wrapper</td>
                </tr>
                <tr className="border-b hover:bg-muted/50">
                  <td className="py-2 px-2 font-mono">loading</td>
                  <td className="py-2 px-2">boolean</td>
                  <td className="py-2 px-2">false</td>
                  <td className="py-2 px-2">Show loading spinner overlay</td>
                </tr>
                <tr className="border-b hover:bg-muted/50">
                  <td className="py-2 px-2 font-mono">empty</td>
                  <td className="py-2 px-2">boolean</td>
                  <td className="py-2 px-2">false</td>
                  <td className="py-2 px-2">Show empty state message</td>
                </tr>
                <tr className="hover:bg-muted/50">
                  <td className="py-2 px-2 font-mono">formatValue</td>
                  <td className="py-2 px-2">(n: number) =&gt; string</td>
                  <td className="py-2 px-2">—</td>
                  <td className="py-2 px-2">Format values in tooltips (e.g., currency)</td>
                </tr>
              </tbody>
            </table>
          </SectionCard>
        </Section>
      </div>
    </ChartProvider>
  );
}

export { DocsChartsPage as Charts };
